import {
  SHBox,
  SHStack,
  SHToggleButton,
  SHToggleButtonGroup,
  SHToggleButtonGroupProps,
  SHToggleButtonProps,
  SHTypography,
} from "@components/design-systems";
import { ToggleButtonOptions } from "@components/design-systems/sh-toggle-button-options/model";
import { TooltipAllowSVG } from "@components/svgs";
import {
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
  Zoom,
} from "@mui/material";
import { hexToRGBA } from "@utils";
import { isEmpty } from "lodash";
import { useState } from "react";
import { configTooltipStyle } from "@components/buttons/information/config";
import { DELAY_TIME } from "@constants";

export type SHToggleButtonOptionsProps = (
  | SHToggleButtonGroupProps
  | SHToggleButtonProps
) & {
  width?: number | string;
  tooltipPlacement?: TooltipProps["placement"];
  tooltipSx?: SHToggleButtonGroupProps["sx"];
  options?: ToggleButtonOptions[];
  activeStartIcon?: JSX.Element;
  activeEndIcon?: JSX.Element;
  inactiveStartIcon?: JSX.Element;
  inactiveEndIcon?: JSX.Element;
  labelFontSize?: number | string;
  labelLineHeight?: number | string;
};

export const SHToggleButtonOptions = (
  props: SHToggleButtonOptionsProps,
): JSX.Element => {
  const {
    options,
    width,
    tooltipPlacement = "top-start",
    tooltipSx,
    onChange,
    value,
    disabled,
    activeStartIcon,
    activeEndIcon,
    inactiveStartIcon,
    inactiveEndIcon,
    labelFontSize,
    labelLineHeight,
  } = props;
  const { palette, shadows } = useTheme();

  const [open, setOpen] = useState(false);
  const showStartIcon = !!activeStartIcon || !!inactiveStartIcon;
  const showEndIcon = !!activeEndIcon || !!inactiveEndIcon;

  const TooltipStyled = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "transparent",
      border: "none",
      p: 0,
      m: 0,
    },
  }));

  return (
    <SHToggleButtonGroup
      disabled={disabled}
      value={value}
      exclusive
      onChange={onChange}
      data-testid="sh-toggle-button-options"
      sx={{
        borderColor: disabled ? palette.text.disabled : undefined,
      }}
    >
      {options?.map((item: ToggleButtonOptions, index) => {
        const isActive = value === item.value;

        const toggleButton = (
          <SHToggleButton
            value={item.value}
            sx={{ width: width }}
            key={index}
            disabled={item.disabled}
          >
            {showStartIcon && (
              <SHStack width={"100%"} alignItems={"start"}>
                {isActive ? activeStartIcon : inactiveStartIcon}
              </SHStack>
            )}
            <SHTypography
              fontSize={labelFontSize}
              variant="subtitle2"
              pt={0}
              lineHeight={labelLineHeight}
            >
              {item.label}
            </SHTypography>
            {showEndIcon && (
              <SHStack width={"100%"} alignItems={"end"}>
                {isActive ? activeEndIcon : inactiveEndIcon}
              </SHStack>
            )}
          </SHToggleButton>
        );

        return !isEmpty(item?.tooltip) ? (
          <TooltipStyled
            enterDelay={DELAY_TIME}
            enterNextDelay={DELAY_TIME}
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            TransitionComponent={Zoom}
            placement={tooltipPlacement}
            title={
              <SHStack
                className={"SHToggleButtonOptions-tooltip"}
                sx={{
                  bgcolor: hexToRGBA(palette.common.white, 0.9),
                  border: "1px solid #E3E3E3",
                  backdropFilter: "blur(5px)",
                  borderRadius: "3px",
                  boxShadow: shadows[1],
                  position: "relative",
                  p: 1,
                  marginLeft: "20px",
                  marginBottom: "-8px",
                  width: "180px",
                  ...tooltipSx,
                }}
              >
                <SHTypography
                  variant="body4"
                  lineHeight={"120%"}
                  colorVariant="third"
                >
                  {item.tooltip}
                </SHTypography>
                <SHBox
                  sx={{
                    ...configTooltipStyle[tooltipPlacement]?.sx,
                    bottom: "-11.7px",
                  }}
                >
                  <TooltipAllowSVG />
                </SHBox>
              </SHStack>
            }
          >
            <SHStack sx={{ cursor: "pointer" }}>{toggleButton}</SHStack>
          </TooltipStyled>
        ) : (
          toggleButton
        );
      })}
    </SHToggleButtonGroup>
  );
};
